import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators, FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { CardModule } from 'primeng/card';
import { PasswordModule } from 'primeng/password';
import { ButtonModule } from 'primeng/button';
import { MessageModule } from 'primeng/message';
import { DividerModule } from 'primeng/divider';
import { PasswordsService } from '../core/passwords.service';
import { AuthService } from '../core/auth.service';

@Component({
  standalone: true,
  selector: 'app-change-password',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
    CardModule,
    PasswordModule,
    ButtonModule,
    MessageModule,
    DividerModule,
  ],
  template: `
  <div class="cp-wrapper">
    <p-card styleClass="cp-card">
      <div class="cp-header">
        <div class="cp-icon"><i class="pi pi-lock"></i></div>
        <div>
          <h2 class="cp-title">{{ 'CHANGE_PASSWORD.TITLE' | translate }}</h2>
          <p class="cp-subtitle">
            قم بتعيين كلمة مرور جديدة لحسابك. يُنصح باستخدام كلمة قوية تختلف عن السابقة.
          </p>
        </div>
      </div>

      <p-divider styleClass="cp-divider"></p-divider>

      <form [formGroup]="form" (ngSubmit)="save()" class="cp-form p-fluid">
        <div class="cp-field">
          <label class="cp-label">{{ 'CHANGE_PASSWORD.CURRENT' | translate }}</label>
          <p-password formControlName="currentPassword"
                      [feedback]="false"
                      [toggleMask]="true"
                      autocomplete="current-password"
                      placeholder="••••••••"
                      styleClass="cp-pwd"
                      inputStyleClass="cp-pwd-input"></p-password>
          @if (f['currentPassword'].touched && f['currentPassword'].hasError('required')) {
            <small class="cp-err">هذا الحقل مطلوب</small>
          }
        </div>

        <div class="cp-field">
          <label class="cp-label">{{ 'CHANGE_PASSWORD.NEW' | translate }}</label>
          <p-password formControlName="newPassword"
                      [toggleMask]="true"
                      [feedback]="true"
                      promptLabel="أدخل كلمة المرور"
                      weakLabel="ضعيفة"
                      mediumLabel="متوسطة"
                      strongLabel="قوية"
                      autocomplete="new-password"
                      placeholder="••••••••"
                      styleClass="cp-pwd"
                      inputStyleClass="cp-pwd-input"></p-password>
          @if (f['newPassword'].touched && f['newPassword'].hasError('required')) {
            <small class="cp-err">هذا الحقل مطلوب</small>
          }
          @if (f['newPassword'].touched && f['newPassword'].hasError('minlength')) {
            <small class="cp-err">يجب أن تكون 6 أحرف على الأقل</small>
          }
        </div>

        <div class="cp-field">
          <label class="cp-label">تأكيد كلمة المرور الجديدة</label>
          <p-password formControlName="confirmPassword"
                      [feedback]="false"
                      [toggleMask]="true"
                      autocomplete="new-password"
                      placeholder="••••••••"
                      styleClass="cp-pwd"
                      inputStyleClass="cp-pwd-input"></p-password>
          @if (f['confirmPassword'].touched && f['confirmPassword'].hasError('required')) {
            <small class="cp-err">هذا الحقل مطلوب</small>
          }
          @if (f['confirmPassword'].touched && passwordsMismatch) {
            <small class="cp-err">كلمتا المرور غير متطابقتين</small>
          }
        </div>

        <div class="cp-hint">
          <i class="pi pi-info-circle"></i>
          <span>اختر كلمة مرور قوية تحتوي على أحرف وأرقام ورموز خاصة.</span>
        </div>

        @if (error) {
          <p-message severity="error" [text]="error" styleClass="cp-msg"></p-message>
        }

        <p-divider styleClass="cp-divider"></p-divider>

        <div class="cp-actions">
          <p-button label="إلغاء"
                    icon="pi pi-times"
                    severity="secondary"
                    [outlined]="true"
                    type="button"
                    styleClass="cp-cancel"
                    (onClick)="cancel()"></p-button>
          <p-button [label]="loading ? 'جارٍ الحفظ...' : ('CHANGE_PASSWORD.SAVE' | translate)"
                    [icon]="loading ? 'pi pi-spin pi-spinner' : 'pi pi-save'"
                    type="submit"
                    styleClass="cp-save"
                    [disabled]="form.invalid || passwordsMismatch || loading"></p-button>
        </div>
      </form>
    </p-card>
  </div>
  `,
  styles: [`
    :host { display: block; }

    .cp-wrapper {
      display: flex;
      justify-content: center;
      align-items: flex-start;
      padding: 32px 16px;
    }

    ::ng-deep .cp-card {
      width: 100%;
      max-width: 600px;
      border-radius: 16px !important;
      box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08) !important;
      background: #fff;
    }
    ::ng-deep .cp-card .p-card-body { padding: 32px; }
    ::ng-deep .cp-card .p-card-content { padding: 0; }

    .cp-header {
      display: flex;
      gap: 16px;
      align-items: flex-start;
    }

    .cp-icon {
      flex: 0 0 auto;
      width: 52px;
      height: 52px;
      border-radius: 12px;
      background: linear-gradient(135deg, #2c5f8d 0%, #1a4d7a 100%);
      display: flex;
      align-items: center;
      justify-content: center;
      box-shadow: 0 4px 12px rgba(26, 77, 122, 0.25);
    }
    .cp-icon i {
      color: #fff;
      font-size: 22px;
    }

    .cp-title {
      margin: 0 0 4px;
      font-size: 20px;
      font-weight: 700;
      color: #1a4d7a;
    }

    .cp-subtitle {
      margin: 0;
      color: #6b7a8a;
      font-size: 13.5px;
      line-height: 1.5;
    }

    ::ng-deep .cp-divider {
      margin: 18px 0 !important;
    }

    .cp-form {
      display: flex;
      flex-direction: column;
      gap: 18px;
    }

    .cp-field {
      display: flex;
      flex-direction: column;
      gap: 6px;
    }

    .cp-label {
      font-size: 13.5px;
      font-weight: 600;
      color: #2c3e50;
    }

    ::ng-deep .cp-pwd { width: 100%; }
    ::ng-deep .cp-pwd .p-password { width: 100%; }
    ::ng-deep .cp-pwd-input {
      width: 100%;
      height: 42px;
      border-radius: 8px !important;
      padding-inline-end: 38px !important;
    }
    ::ng-deep .cp-pwd .p-password-toggle-mask-icon,
    ::ng-deep .cp-pwd .p-icon-wrapper { color: #6b7a8a; }

    .cp-err {
      color: #b00020;
      font-size: 12.5px;
    }

    .cp-hint {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 10px 12px;
      border-radius: 8px;
      background: #f1f7fb;
      color: #4a6275;
      font-size: 12.5px;
    }
    .cp-hint i {
      font-size: 16px;
      color: #2c5f8d;
    }

    ::ng-deep .cp-msg {
      width: 100%;
    }
    ::ng-deep .cp-msg .p-inline-message {
      width: 100%;
      justify-content: flex-start;
      border-radius: 8px;
    }

    .cp-actions {
      display: flex;
      justify-content: flex-end;
      gap: 12px;
    }

    ::ng-deep .cp-cancel .p-button {
      min-width: 110px;
      height: 42px;
      border-radius: 8px;
    }

    ::ng-deep .cp-save .p-button {
      min-width: 170px;
      height: 42px;
      border-radius: 8px;
      font-weight: 600;
      background: linear-gradient(135deg, #8b7355 0%, #7b6b94 100%);
      border-color: transparent;
      color: #fff;
      box-shadow: 0 4px 12px rgba(139, 115, 85, 0.3);
      transition: transform .15s ease, box-shadow .15s ease;
    }
    ::ng-deep .cp-save .p-button:not(:disabled):hover {
      background: linear-gradient(135deg, #7b6b94 0%, #8b7355 100%);
      transform: translateY(-1px);
      box-shadow: 0 6px 16px rgba(139, 115, 85, 0.4);
    }
    ::ng-deep .cp-save .p-button:disabled { opacity: 0.6; }

    @media (max-width: 600px) {
      ::ng-deep .cp-card .p-card-body { padding: 22px; }
      .cp-header { gap: 12px; }
      .cp-icon { width: 44px; height: 44px; }
      .cp-icon i { font-size: 20px; }
      .cp-title { font-size: 18px; }
      .cp-actions { flex-direction: column-reverse; }
      ::ng-deep .cp-actions .p-button { width: 100%; }
    }
  `],
})
export class ChangePasswordComponent implements OnInit {
  private api = inject(PasswordsService);
  private auth = inject(AuthService);
  private ms = inject(MessageService);
  private translate = inject(TranslateService);
  private fb = inject(FormBuilder);
  private router = inject(Router);

  form!: FormGroup;
  loading = false;
  error = '';

  get userId(): string | null {
    return this.auth.claims?.sub || null;
  }

  get f() { return this.form.controls; }

  get passwordsMismatch(): boolean {
    const a = this.form?.get('newPassword')?.value;
    const b = this.form?.get('confirmPassword')?.value;
    return !!a && !!b && a !== b;
  }

  ngOnInit() {
    this.form = this.fb.group({
      currentPassword: ['', [Validators.required]],
      newPassword: ['', [Validators.required, Validators.minLength(6)]],
      confirmPassword: ['', [Validators.required]],
    });
  }

  cancel() {
    this.router.navigateByUrl('/');
  }

  save() {
    this.error = '';
    if (!this.userId || this.form.invalid || this.passwordsMismatch) return;

    const currentPassword = this.f['currentPassword'].value as string;
    const newPassword = this.f['newPassword'].value as string;

    if (newPassword === currentPassword) {
      this.error = 'كلمة المرور الجديدة يجب أن تختلف عن الحالية.';
      return;
    }

    this.loading = true;
    this.api.change(this.userId, currentPassword, newPassword).subscribe({
      next: () => {
        this.loading = false;
        this.ms.add({
          severity: 'success',
          summary: this.translate.instant('COMMON.SUCCESS'),
          detail: this.translate.instant('CHANGE_PASSWORD.SUCCESS'),
        });
        this.form.reset();
      },
      error: (e) => {
        this.loading = false;
        const errs = e?.error?.errors;
        if (errs) {
          const messages: string[] = [];
          for (const v of Object.values(errs) as unknown[]) {
            if (Array.isArray(v)) messages.push(...(v as string[]));
            else if (typeof v === 'string') messages.push(v);
          }
          this.error = messages.join(' • ');
        } else {
          this.error = e?.error?.message || this.translate.instant('CHANGE_PASSWORD.ERROR');
        }
      },
    });
  }
}
